import React, { useEffect, useMemo, useState } from 'react'
import template from 'lodash/template'
import i18n from 'simple-react-i18n'
import PropTypes from 'prop-types'
import { Grid, Card, CardContent } from '@mui/material'
import Select from 'components/forms/Select'
import RadioButtons from '../../../components/forms/RadioButtons'
import { numberList } from 'utils/ListUtil'
import { useDispatch } from 'react-redux'

const RECURRENCE_TYPE_WEEKLY = 'weekly'
const RECURRENCE_TYPE_MONTHLY = 'monthly'

const ParamTraitementSynchroImport = ({
    valid,
    setPairToSaveSynchro,
    insertAllDataBanq,
    data,
}) => {
    const dispatch = useDispatch()

    const params = JSON.parse(data.parameters)
    const id = data.id
    const name = data.name

    const cronParts = data.cron?.split(' ') || []

    const day = cronParts[5] || '6'
    const hour = cronParts[2] || '0'
    const minute = cronParts[1] || '0'

    const [days, setDays] = useState(day)
    const [hours, setHours] = useState(hour)
    const [minutes, setMinutes] = useState(minute)
    const [monthDay, setMonthDay] = useState(data.days || '1')
    const [recurrence, setRecurrence] = useState(RECURRENCE_TYPE_WEEKLY)


    const cron = useMemo(() => {
        const dayOfMonth = recurrence === RECURRENCE_TYPE_MONTHLY? monthDay: '*'
        const dayOfWeek = recurrence === RECURRENCE_TYPE_MONTHLY? '*': days
        return ['0', minutes, hours, dayOfMonth, '*', dayOfWeek, '*'].join(' ')
    }, [recurrence, monthDay, days, hours, minutes])

    const initialJob = useMemo(() => ({ id, name, parameters: JSON.stringify(params), cron, days, hours, minutes }), [cron, days, hours, minutes])


    const hoursOptions = useMemo(() => [ { value: '*', label: i18n.everyHours }, ...numberList(24) ], [])
    const minutesOptions = useMemo(() => [
        { value: '*/5', label: template(i18n.everyCustomMinutes)({ minute: 5 }) },
        { value: '*/10', label: template(i18n.everyCustomMinutes)({ minute: 10 }) },
        { value: '*/15', label: template(i18n.everyCustomMinutes)({ minute: 15 }) },
        { value: '*/20', label: template(i18n.everyCustomMinutes)({ minute: 20 }) },
        { value: '*/30', label: template(i18n.everyCustomMinutes)({ minute: 30 }) },
        ...numberList(60),
    ], [])

    const daysOptions = useMemo(() => [
        { value: '*', label: i18n.allDays },
        { value: '1', label: i18n.monday },
        { value: '2', label: i18n.tuesday },
        { value: '3', label: i18n.wednesday, disabled: true },
        { value: '4', label: i18n.thursday },
        { value: '5', label: i18n.friday },
        { value: '6', label: i18n.saturday },
        { value: '0', label: i18n.sunday },
    ], [])

    const monthDayOptions = useMemo(() => numberList(32).slice(1), [])

    const recurrenceOptions = [
        { code: RECURRENCE_TYPE_WEEKLY, name: i18n.weekly },
        { code: RECURRENCE_TYPE_MONTHLY, name: i18n.monthly },
    ]

    const getInsert = () => {
        if (!params.filters?.length) {
            return undefined
        }
        const filter = JSON.parse(params.filters[0])
        return filter.insertAllDataBanq
    }

    const [insertValue, setInsertValue] = useState(getInsert())


    useEffect(() => {
        if (valid || !params.jobType) {
            return
        }

        const updated1 = {
            ...params,
            filters: [JSON.stringify({ insertAllDataBanq: insertValue })],
        }
        const updated2 = {
            initialJob,
            dispatch,
        }
        const updatedJob = {
            initialJob: { ...updated2.initialJob, parameters: updated1 },
            dispatch,
        }
        setPairToSaveSynchro(prev => {
            if (!params.jobType) return prev
            return {
                ...prev,
                [params.jobType]: updatedJob,
            }
        })
    }, [valid, hours, minutes, recurrence, days, monthDay, insertValue])

    useEffect(() => {
        if (!valid || !params.jobType) {
            return
        }


        setPairToSaveSynchro(prev => {
            // eslint-disable-next-line no-unused-vars
            const { [params.jobType]: deletedValue, ...next } = prev
            return next
        })
    }, [valid, params.jobType])


    const getIntegrationModes = () => [
        {
            value: true,
            label: i18n.importAllReferentialAvailable,
        },
        {
            value: false,
            label: i18n.updateOnly,
        },
    ]

    return (
        <Card>
            <CardContent>
                <Grid container spacing={2}>
                    <Grid size={3}>
                        { i18n.recurrence }
                        <RadioButtons
                            elements={ recurrenceOptions }
                            selected={ recurrence }
                            onChange={ (v) => setRecurrence(v) }
                        />
                    </Grid>
                    <Grid size={2}>
                        <Select
                            value={ hours || 0 }
                            options={ hoursOptions }
                            label={ i18n.jobHour }
                            onChange={ (v) => setHours(v) }
                            noNullValue
                        />
                    </Grid>
                    <Grid size={2}>
                        <Select
                            value={ minutes || 0 }
                            options={ minutesOptions }
                            label={ i18n.jobMinute }
                            onChange={ (v) => setMinutes(v) }
                            noNullValue
                        />
                    </Grid>
                    <Grid size={2}>
                        <Select
                            value={ recurrence === RECURRENCE_TYPE_MONTHLY ? monthDay : days }
                            options={ recurrence === RECURRENCE_TYPE_MONTHLY ? monthDayOptions : daysOptions }
                            label={ recurrence === RECURRENCE_TYPE_MONTHLY ? i18n.day : i18n.dayOfWeek }
                            onChange={ (v) => recurrence === RECURRENCE_TYPE_MONTHLY ? setMonthDay(v) : setDays(v) }
                            noNullValue
                            noSort
                        />
                    </Grid>
                    <Grid size={3}>
                        <Select
                            value={insertValue ?? insertAllDataBanq}
                            options={getIntegrationModes()}
                            label={i18n.dataIntegration}
                            onChange={v => setInsertValue(v)}
                        />
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}


ParamTraitementSynchroImport.propTypes = {
    valid: PropTypes.bool,
    setPairToSaveSynchro: PropTypes.func,
    data: PropTypes.array,
    insertAllDataBanq: PropTypes.any,
}


export default ParamTraitementSynchroImport